import type {
  CommercialCalibration,
  CommercialParameters,
  CommercialScenarioInput,
  CommercialValidationIssue,
} from "@/api/commercialCalibration";

/** Até três cenários lado a lado (EF-FUNC-012 v0.1 RF-05). */
export const SCENARIO_COUNT = 3;

type ScenarioNumericField = Exclude<keyof CommercialScenarioInput, "name" | "take_to_diagnostic">;

/**
 * O que a pessoa digita. Campo numérico apagado fica `null`, e não 0: vazio é
 * erro no campo, e 0 é um valor que vai ao Diagnóstico.
 */
export type CommercialForm = {
  parameters: { [K in keyof CommercialParameters]: number | null };
  scenarios: (Omit<CommercialScenarioInput, ScenarioNumericField> & {
    [K in ScenarioNumericField]: number | null;
  })[];
};

/** O corpo da prévia, e o da gravação sem as referências. */
export type FilledCommercialForm = {
  parameters: CommercialParameters;
  scenarios: CommercialScenarioInput[];
};

/** O formulário pronto para a API, ou `null` enquanto algum campo numérico está vazio. */
export function filledForm(form: CommercialForm): FilledCommercialForm | null {
  const values = [
    ...Object.values(form.parameters),
    ...form.scenarios.flatMap((scenario) => [
      scenario.target_margin_pct,
      scenario.hyperscale_use_pct,
      scenario.consider_selection_use_pct,
      scenario.adopt_operation_use_pct,
    ]),
  ];
  return values.some((value) => value === null) ? null : (form as FilledCommercialForm);
}

/** O valor do campo numérico: vazio vira `null`, e não o 0 do `Number("")`. */
export function numberOrNull(raw: string): number | null {
  return raw.trim() === "" ? null : Number(raw);
}

/**
 * De onde a tela parte: a última versão salva, ou os padrões da API com três
 * cenários. O cenário novo nasce com a margem alvo padrão (45%) e sem usar
 * funding nenhum; quem calibra liga o que vai oferecer.
 */
export function initialForm(
  data: CommercialCalibration,
  scenarioName: (position: number) => string,
): CommercialForm {
  if (data.latest) {
    return {
      parameters: { ...data.latest.parameters },
      scenarios: data.latest.scenarios.map(
        ({ position: _position, result: _result, ...input }) => input,
      ),
    };
  }
  return {
    parameters: { ...data.defaults.parameters },
    scenarios: Array.from({ length: SCENARIO_COUNT }, (_, index) => ({
      name: scenarioName(index + 1),
      target_margin_pct: data.defaults.target_margin_pct,
      hyperscale_use_pct: 0,
      consider_selection_use_pct: 0,
      adopt_operation_use_pct: 0,
      take_to_diagnostic: false,
    })),
  };
}

/**
 * Os erros de um campo: dos parâmetros (`scenario` nulo) ou de um cenário, pela
 * posição que começa em 1. É o par que a API usa para dizer onde está o erro.
 */
export function fieldIssues(
  issues: CommercialValidationIssue[] | undefined,
  field: string,
  scenario: number | null = null,
): CommercialValidationIssue[] {
  return (issues ?? []).filter(
    (issue) => issue.field === field && (issue.scenario ?? null) === scenario,
  );
}
